import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useConvexAuth } from "convex/react";
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Users, Key, Activity, TrendingUp, Clock, Mail, Shield, Globe, Terminal, BarChart3, Lock } from "lucide-react";
import { SEO } from "../components/SEO";

const ADMIN_EMAIL = "veronata@veronata.com";

function AccessDenied() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
        <h1 className="text-xl font-bold mb-2">Access Denied</h1>
        <p className="text-text-muted">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  subtext,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="bg-surface rounded-lg p-4 border border-border">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-brand/10 rounded-lg">
          <Icon className="w-5 h-5 text-brand" />
        </div>
        <span className="text-sm text-text-muted">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {subtext && <div className="text-xs text-text-muted mt-1">{subtext}</div>}
    </div>
  );
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Admin() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  // First, check if user is admin by fetching their own user record
  const me = useQuery(api.users.getMe);

  // Only fetch admin data if user is confirmed admin
  const stats = useQuery(
    api.mcpAdmin.getAdminStats,
    isAdmin === true ? {} : "skip"
  );
  const recentUsage = useQuery(
    api.mcpAdmin.getRecentApiUsage,
    isAdmin === true ? { limit: 20 } : "skip"
  );
  const analytics = useQuery(
    api.mcpAdmin.getMcpAnalytics,
    isAdmin === true ? { hours: 24 } : "skip"
  );

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setIsAdmin(false);
      return;
    }

    if (me !== undefined) {
      const user = me;
      const hasAdminAccess = user?.email === ADMIN_EMAIL || user?.isAdmin === true;
      setIsAdmin(hasAdminAccess);
    }
  }, [authLoading, isAuthenticated, me]);

  if (authLoading || isAdmin === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <AccessDenied />;
  }

  if (!stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mb-4" />
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Admin | StarSinger MCP"
        description="StarSinger MCP Admin Dashboard - Server analytics and user management."
        pathname="/admin"
      />
      <div className="min-h-[70vh] px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-brand" />
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-text-muted">MCP Server Analytics</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="MCP Users"
            value={stats.users.total}
            subtext={`+${stats.users.last7Days} this week`}
          />
          <StatCard
            icon={Key}
            label="API Keys"
            value={stats.apiKeys.total}
            subtext={`${stats.apiKeys.active} active`}
          />
          <StatCard
            icon={Activity}
            label="Total Requests"
            value={stats.apiKeys.totalRequests.toLocaleString()}
          />
          <StatCard
            icon={TrendingUp}
            label="Subscriptions"
            value={stats.subscriptions.total}
            subtext={`${stats.subscriptions.active} active`}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent MCP Users */}
          <div className="bg-surface rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Mail className="w-4 h-4 text-text-muted" />
              <h2 className="font-semibold">Recent MCP Users</h2>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {stats.recentSignups.length === 0 ? (
                <div className="px-4 py-8 text-center text-text-muted text-sm">No signups yet</div>
              ) : (
                stats.recentSignups.map((user) => (
                  <div key={user.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{user.name || user.email || "Anonymous"}</div>
                      <div className="text-xs text-text-muted">{user.email}</div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          user.provider === "google" ? "bg-blue-500/20 text-blue-400" : "bg-gray-500/20 text-gray-400"
                        }`}
                      >
                        {user.provider}
                      </span>
                      <div className="text-xs text-text-muted mt-1">{formatDate(user.createdAt)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent API Keys */}
          <div className="bg-surface rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Key className="w-4 h-4 text-text-muted" />
              <h2 className="font-semibold">Recent API Keys</h2>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {stats.recentApiKeys.length === 0 ? (
                <div className="px-4 py-8 text-center text-text-muted text-sm">No API keys created yet</div>
              ) : (
                stats.recentApiKeys.map((key) => (
                  <div key={key.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{key.name}</div>
                      <div className="text-xs text-text-muted">{key.email}</div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          key.tier === "free"
                            ? "bg-gray-500/20 text-gray-400"
                            : key.tier === "pro"
                              ? "bg-brand/20 text-brand"
                              : "bg-purple-500/20 text-purple-400"
                        }`}
                      >
                        {key.tier}
                      </span>
                      <div className="text-xs text-text-muted mt-1">
                        {key.requestCount.toLocaleString()} requests
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Detailed MCP Analytics */}
        {analytics && (
          <>
            <div className="mt-8 flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-brand" />
              <div>
                <h2 className="text-xl font-bold">MCP Analytics (Last 24h)</h2>
                <p className="text-sm text-text-muted">{analytics.totalRequests.toLocaleString()} requests from {analytics.uniqueUsers} unique users</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
              {/* Endpoint Breakdown */}
              <div className="bg-surface rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-text-muted" />
                  <h2 className="font-semibold">Endpoint Usage</h2>
                </div>
                <div className="divide-y divide-border max-h-80 overflow-y-auto">
                  {Object.entries(analytics.endpointStats).length === 0 ? (
                    <div className="px-4 py-8 text-center text-text-muted text-sm">No requests yet</div>
                  ) : (
                    Object.entries(analytics.endpointStats)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([endpoint, stats]) => (
                        <div key={endpoint} className="px-4 py-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{endpoint.replace('/api/mcp/', '')}</span>
                            <span className="text-sm">{stats.count.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-text-muted">
                            <span>{stats.avgDuration}ms avg</span>
                            {stats.errors > 0 && (
                              <span className="text-red-400">{stats.errors} errors</span>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* User Agent Breakdown */}
              <div className="bg-surface rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <Globe className="w-4 h-4 text-text-muted" />
                  <h2 className="font-semibold">Clients (User-Agent)</h2>
                </div>
                <div className="divide-y divide-border max-h-80 overflow-y-auto">
                  {Object.entries(analytics.userAgentStats).length === 0 ? (
                    <div className="px-4 py-8 text-center text-text-muted text-sm">No data yet</div>
                  ) : (
                    Object.entries(analytics.userAgentStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([agent, count]) => (
                        <div key={agent} className="px-4 py-3 flex items-center justify-between">
                          <span className="text-sm">{agent}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* Status Code Breakdown */}
              <div className="bg-surface rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <Activity className="w-4 h-4 text-text-muted" />
                  <h2 className="font-semibold">Response Status</h2>
                </div>
                <div className="divide-y divide-border max-h-80 overflow-y-auto">
                  {Object.entries(analytics.statusStats).length === 0 ? (
                    <div className="px-4 py-8 text-center text-text-muted text-sm">No data yet</div>
                  ) : (
                    Object.entries(analytics.statusStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([code, count]) => (
                        <div key={code} className="px-4 py-3 flex items-center justify-between">
                          <span className={`text-sm ${code.startsWith('2') ? 'text-green-400' : code.startsWith('4') ? 'text-yellow-400' : 'text-red-400'}`}>
                            {code}
                          </span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Recent Requests Log */}
            <div className="mt-6 bg-surface rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Clock className="w-4 h-4 text-text-muted" />
                <h2 className="font-semibold">Recent Requests (Last 50)</h2>
              </div>
              <div className="divide-y divide-border max-h-96 overflow-y-auto">
                {analytics.recentRequests.length === 0 ? (
                  <div className="px-4 py-8 text-center text-text-muted text-sm">No requests yet</div>
                ) : (
                  analytics.recentRequests.map((req) => (
                    <div key={req.id} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{req.endpoint.replace('/api/mcp/', '')}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${req.statusCode === 200 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {req.statusCode}
                          </span>
                        </div>
                        <span className="text-xs text-text-muted">{formatDate(req.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-muted">
                        <span>{req.durationMs}ms</span>
                        {req.userAgent && <span>{req.userAgent.split(' ')[0]}</span>}
                        {req.ipAddress && <span>IP: {req.ipAddress}</span>}
                      </div>
                      {req.errorMessage && (
                        <div className="text-xs text-red-400 mt-1">{req.errorMessage}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* API Usage (Legacy) */}
        <div className="mt-6 bg-surface rounded-lg border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Clock className="w-4 h-4 text-text-muted" />
            <h2 className="font-semibold">API Keys by Usage</h2>
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {!recentUsage || recentUsage.length === 0 ? (
              <div className="px-4 py-8 text-center text-text-muted text-sm">No API usage yet</div>
            ) : (
              recentUsage.map((key) => (
                <div key={key.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{key.name}</div>
                    <div className="text-xs text-text-muted">{key.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">{key.requestCount.toLocaleString()} requests</div>
                    {key.lastUsedAt && (
                      <div className="text-xs text-text-muted">Last used: {formatDate(key.lastUsedAt)}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
